/**
 * Officer Service
 * 
 * Handles all officer-related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

const officerService = {
  /**
   * Get list of officers
   * @param {Object} params - Query parameters (page, limit, status, etc.)
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>} List of officers
   */
  async getOfficers(params = {}, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.list 
        : API_ENDPOINTS.officers.list;
      const response = await apiClient.get(endpoint, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch officers' });
    }
  },

  /**
   * Get officer by ID
   * @param {string} id - Officer ID
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>} Officer details
   */
  async getOfficer(id, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.detail(id) 
        : API_ENDPOINTS.officers.detail(id);
      const response = await apiClient.get(endpoint);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch officer details' });
    }
  },

  /**
   * Create new officer
   * @param {Object} payload - Officer payload in API format
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>} Created officer
   */
  async createOfficer(payload, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.create 
        : API_ENDPOINTS.officers.create;
      const response = await apiClient.post(endpoint, payload);
      return {
        success: true,
        data: response.data,
        message: 'Officer created successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to create officer' });
    }
  },

  /**
   * Update officer (partial update)
   * @param {string} id - Officer ID
   * @param {Object} payload - Officer payload in API format (partial fields allowed)
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>} Updated officer
   */
  async updateOfficer(id, payload, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.update(id) 
        : API_ENDPOINTS.officers.update(id);
      const response = await apiClient.patch(endpoint, payload);
      return {
        success: true,
        data: response.data,
        message: 'Officer updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update officer' });
    }
  },

  /**
   * Delete officer
   * @param {string} id - Officer ID
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>}
   */
  async deleteOfficer(id, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.delete(id) 
        : API_ENDPOINTS.officers.delete(id);
      await apiClient.delete(endpoint);
      return {
        success: true,
        message: 'Officer deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete officer' });
    }
  },

  /**
   * Activate officer
   * @param {string} id - Officer ID
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>}
   */
  async activateOfficer(id, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.activate(id) 
        : API_ENDPOINTS.officers.activate(id);
      const response = await apiClient.post(endpoint);
      return {
        success: true,
        data: response.data,
        message: 'Officer activated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to activate officer' });
    }
  },

  /**
   * Deactivate officer
   * @param {string} id - Officer ID
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>}
   */
  async deactivateOfficer(id, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.deactivate(id) 
        : API_ENDPOINTS.officers.deactivate(id);
      const response = await apiClient.post(endpoint);
      return {
        success: true,
        data: response.data,
        message: 'Officer deactivated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to deactivate officer' });
    }
  },

  /**
   * Get officer's assigned cases
   * @param {string} id - Officer ID
   * @param {Object} params - Query parameters
   * @param {string} role - 'cvo' or 'authority' (default: 'cvo')
   * @returns {Promise<Object>} List of cases
   */
  async getOfficerCases(id, params = {}, role = 'cvo') {
    try {
      const endpoint = role === 'authority' 
        ? API_ENDPOINTS.authority.officers.cases(id) 
        : API_ENDPOINTS.officers.cases(id);
      const response = await apiClient.get(endpoint, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch officer cases' });
    }
  },
};

export default officerService;

